import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { track } from "./telemetry/appinsights.js";
import { readOwnedUserProfileCache } from "./utils/userProfileCache";
import './index.css'; 

function HeroGallery({
  images = [],
  stageHeight = 320,
  objectFit = "contain",
  intervalMs = 2000,
}) {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [requestedSlides, setRequestedSlides] = useState(() => new Set([0]));
  const [readySlides, setReadySlides] = useState(() => new Set());
  const [failedSlides, setFailedSlides] = useState(() => new Set());
  const pendingSlideRef = useRef(null);
  const hasImages = images?.length > 0;

  // --- slideshow timer ---
  const timeoutRef = useRef(null);
  const clearTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const requestSlide = useCallback((slideIndex) => {
    if (!hasImages) return;
    const normalizedIndex = (slideIndex + images.length) % images.length;
    setRequestedSlides((previous) => {
      if (previous.has(normalizedIndex)) return previous;
      const next = new Set(previous);
      next.add(normalizedIndex);
      return next;
    });
  }, [hasImages, images.length]);

  const scrollToSlide = useCallback((next) => {
    const el = trackRef.current;
    if (!el) return;

    const w = el.clientWidth || 1;
    setIdx(next);
    setIsAnimating(true);
    el.scrollTo({ left: next * w, behavior: "smooth" });

    const onEnd = () => {
      setIsAnimating(false);
      el.removeEventListener?.("scrollend", onEnd);
    };
    if ("onscrollend" in window || "ScrollTimeline" in window) {
      el.addEventListener?.("scrollend", onEnd, { once: true });
    } else {
      setTimeout(onEnd, 350);
    }
  }, []);

  // move-by-one helper (may call revealAround)
  const go = useCallback((dir) => {
    if (!hasImages) return;

    const next = (idx + dir + images.length) % images.length;
    if (failedSlides.has(next)) return;
    requestSlide(next);
    if (readySlides.has(next)) {
      scrollToSlide(next);
      return;
    }
    pendingSlideRef.current = next;
  }, [failedSlides, idx, hasImages, images.length, readySlides, requestSlide, scrollToSlide]);

  const handleSlideReady = useCallback((slideIndex) => {
    setReadySlides((previous) => {
      if (previous.has(slideIndex)) return previous;
      const next = new Set(previous);
      next.add(slideIndex);
      return next;
    });
    if (pendingSlideRef.current === slideIndex) {
      pendingSlideRef.current = null;
      scrollToSlide(slideIndex);
    }
  }, [scrollToSlide]);

  const handleSlideError = useCallback((slideIndex) => {
    setFailedSlides((previous) => {
      if (previous.has(slideIndex)) return previous;
      const next = new Set(previous);
      next.add(slideIndex);
      return next;
    });
    if (pendingSlideRef.current === slideIndex) {
      pendingSlideRef.current = null;
    }
  }, []);
  
  const armTimer = useCallback(() => {
    clearTimer();
    if (!hasImages || !intervalMs || hover) return;
    timeoutRef.current = setTimeout(() => {
      if (!isAnimating) go(1);
      else armTimer(); // retry after current animation
    }, intervalMs);
  }, [hover, intervalMs, hasImages, isAnimating, go, clearTimer]);

  useEffect(() => armTimer(), [armTimer]);
  useEffect(() => () => clearTimer(), [clearTimer]);

  useEffect(() => {
    if (!hasImages) return undefined;

    const loadNextSlide = () => requestSlide(idx + 1);
    let timeoutId = null;
    let idleId = null;

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(loadNextSlide, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(loadNextSlide, 250);
    }

    return () => {
      if (idleId !== null) window.cancelIdleCallback?.(idleId);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [idx, hasImages, requestSlide]);

  // keep idx in sync on manual scroll; also reveal the centered slide
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth || 1;
      const i = Math.round(el.scrollLeft / w);
      if (i !== idx) {
        setIdx(i);
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [idx]);

return (
    <div
      style={sx.galleryWrap}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div
        ref={trackRef}
        style={{ ...sx.galleryTrack, height: stageHeight }}
        onWheel={(e) => {
          // allow vertical wheel to pan horizontally
          if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            e.currentTarget.scrollLeft += e.deltaY;
            e.preventDefault();
          }
        }}
      >
{hasImages ? (
  images.map((src, i) => {
    const shouldRenderImage = requestedSlides.has(i);
    return (
      <div
        key={`slide-wrap-${i}`}
        style={sx.gallerySlide}
      >
        {shouldRenderImage && (
          <img
            src={src}
            alt={`TutorOrca online tutoring preview ${i + 1}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit,
              objectPosition: "center",
              display: "block",
              background: "#fff",
            }}
            loading={i === 0 ? "eager" : "lazy"}
            fetchpriority={i === 0 ? "high" : "low"}
            decoding="async"
            onLoad={() => handleSlideReady(i)}
            onError={() => handleSlideError(i)}
          />
        )}
      </div>
    );
  })
) : (
  <div style={sx.galleryEmpty}>No images</div>
)}
      </div>

      {hasImages && (
        <>
          <button aria-label="Prev" style={{ ...sx.arrow, left: 8 }} onClick={() => go(-1)}>‹</button>
          <button aria-label="Next" style={{ ...sx.arrow, right: 8 }} onClick={() => go(1)}>›</button>
        </>
      )}

      {hasImages && (
        <div style={sx.dots}>
          {images.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => go(i - idx)}
              style={{ ...sx.dot, ...(i === idx ? sx.dotActive : null) }}
            />
          ))}
        </div>
      )}


    </div>
  );
}
// --- Rotating marketing line (shows a random one each time) ---
const marketingLines = [
  "Get personalized 1:1 tutor support tailored to your child’s grade, pace, and goals.",
  "Homework gets easier when a live tutor explains concepts step by step.",
  "Kid-safe and parent-friendly, with a focused learning experience you can trust.",
  "Tutors help children clear doubts, build confidence, and make steady progress.",
  "Clear parent insights help you track performance, priorities, and growth over time."
];

const headlineLines = [
  { pre: "Personalized 1:1 tutoring that builds confidence and stronger grades — for ", highlight: "KG–10", post: "." },
  { pre: "Turn after-school stress into steady progress with expert tutor support — ", highlight: "KG–10", post: "." },
  { pre: "Less homework struggle, more understanding with one-on-one help — ", highlight: "KG–10", post: "." },
  { pre: "Live tutor guidance tailored to your child’s pace, goals, and needs — ", highlight: "KG–10", post: "." },
  { pre: "Stay informed with clear parent updates after each learning session — ", highlight: "KG–10", post: "." },
];

function useDeferredRender(timeoutMs = 1200) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let timeoutId = null;
    let idleId = null;

    const show = () => setReady(true);

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(show, { timeout: timeoutMs });
    } else {
      timeoutId = window.setTimeout(show, 250);
    }

    return () => {
      if (idleId !== null) window.cancelIdleCallback?.(idleId);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [timeoutMs]);

  return ready;
}

function Home({ userName }) {
  const navigate = useNavigate();
  const isSignedIn = Boolean(userName);
  const showLowerHome = useDeferredRender();

const [demoModalOpen, setDemoModalOpen] = useState(false);
const [demoSubject, setDemoSubject] = useState('');
const [demoGrade, setDemoGrade] = useState('');
const [demoEmail, setDemoEmail] = useState('');
const [demoNotify, setDemoNotify] = useState('');
const [demoSubmitting, setDemoSubmitting] = useState(false);

const resetDemoModal = () => {
  setDemoSubject('');
  setDemoGrade('');
  setDemoEmail('');
  setDemoNotify('');
};


const [headlineLine, setHeadlineLine] = useState(headlineLines[0]);

useEffect(() => {
  const randomIndex = Math.floor(Math.random() * headlineLines.length);
  setHeadlineLine(headlineLines[randomIndex]);
}, []);


const [marketingLine, setMarketingLine] = useState("");

useEffect(() => {
  const randomIndex = Math.floor(Math.random() * marketingLines.length);
  setMarketingLine(marketingLines[randomIndex]);
}, []);

const slideVersion = "20260307-1";
const slides = [
  `/slides/snapshot001.jpg?v=${slideVersion}`,
  `/slides/snapshot002.jpg?v=${slideVersion}`,
  `/slides/snapshot003.jpg?v=${slideVersion}`,
  `/slides/snapshot004.jpg?v=${slideVersion}`,
  `/slides/snapshot004_1.jpg?v=${slideVersion}`,
  `/slides/snapshot005.jpg?v=${slideVersion}`,
  `/slides/snapshot006.jpg?v=${slideVersion}`,
  `/slides/snapshot007.jpg?v=${slideVersion}`,
  `/slides/snapshot008.jpg?v=${slideVersion}`,
  `/slides/snapshot009.jpg?v=${slideVersion}`,
  `/slides/snapshot010.jpg?v=${slideVersion}`,
  `/slides/snapshot011.jpg?v=${slideVersion}`,
  `/slides/snapshot012.jpg?v=${slideVersion}`,
  `/slides/snapshot013.jpg?v=${slideVersion}`,
  `/slides/snapshot014.jpg?v=${slideVersion}`,
];

// — responsive: 2-column hero on wide screens —
// — responsive breakpoints —
const getViewportWidth = () =>
  Math.round(window.visualViewport?.width || window.innerWidth || 1024);

const [vw, setVw] = useState(() => getViewportWidth());

useEffect(() => {
  const onR = () => setVw(getViewportWidth());

  window.addEventListener("resize", onR);
  window.visualViewport?.addEventListener("resize", onR);

  return () => {
    window.removeEventListener("resize", onR);
    window.visualViewport?.removeEventListener("resize", onR);
  };
}, []);

const isWide = vw >= 980;
const isTablet = vw < 980 && vw >= 680;
const isPhone = vw < 680;

const goSignup = () => {
  track.ev("CTA_Click", { cta: "StartTutoringAccount", placement: "Hero", ...getTrackingContext() });
  navigate("/signup");
};

const goDashboard = () => {
    try {
      // 1) check simple role keys (what Login.js likely sets)
      const simpleRole =
        window.localStorage.getItem("userType") ||
        window.localStorage.getItem("role");

      let role = simpleRole;

      // 2) fall back to full profile object if present
      if (!role) {
        const profile = readOwnedUserProfileCache();
        role = profile?.role || "";
      }

      const normalized = (role || "").toLowerCase();

      if (normalized === "tutor") {
        navigate("/tutor-dashboard", { replace: true });
      } else {
        navigate("/subscriber-dashboard", { replace: true });
      }
    } catch {
      navigate("/subscriber-dashboard", { replace: true });
    }
  };

const getTrackingContext = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    planType: p.get("plan") || "free",
    utm_source: p.get("utm_source") || "(direct)",
    utm_medium: p.get("utm_medium") || "(none)",
  };
};

// Assume you already have a dealEndTime (Date) or expiration timestamp
// inside Home component, before return()
useEffect(() => {
  track.pv("Home");
  // track.ev("LandingViewed", getTrackingContext());
}, []);


  return (
    <div style={sx.page}>
{/* Top Navigation (no duplicate logo) */}
<div style={{
  background: "linear-gradient(90deg, #4C1D95 0%, #1E3A8A 55%, #0EA5E9 100%)",
  color: "#fff",
  padding: isPhone ? "10px 14px" : "10px 20px",
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "center",
  rowGap: isPhone ? 10 : 8,
  columnGap: 10
}}>

<nav style={{ display: "flex", gap: 16, alignItems: "center" }}>
  <Link
  to="/subject"
  style={{ ...sx.navLink, ...(isPhone ? sx.navLinkPhone : null) }}
    onMouseEnter={e => Object.assign(e.currentTarget.style, sx.navLinkHover)}
    onMouseLeave={e => Object.assign(e.currentTarget.style, { ...sx.navLink, ...(isPhone ? sx.navLinkPhone : null) })}
  >
    Subjects
  </Link>

  <Link to="/features" style={{ ...sx.navLink, ...(isPhone ? sx.navLinkPhone : null) }}>Features</Link>
  <Link to="/about" style={{ ...sx.navLink, ...(isPhone ? sx.navLinkPhone : null) }}>About</Link>
</nav>

  {/* Left: spacer so layout balances without a second logo */}
  <div style={{ width: isPhone ? 0 : 120 }} />

  {/* Right: Actions */}
  <div style={{
  display:"flex",
  alignItems:"center",
  gap: isPhone ? 10 : 16,
  flexWrap: "wrap",
  justifyContent: isPhone ? "flex-start" : "flex-end",
  width: isPhone ? "100%" : "auto"
}}>
<div style={{ display: "flex", alignItems: "center" }}>
<button
  style={{
    background: "linear-gradient(90deg, #facc15 0%, #f97316 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: isPhone ? "8px 16px" : "14px 28px",
    fontWeight: 800,
    fontSize: isPhone ? 13 : 16,
    cursor: "pointer",
    marginRight: 12,
    boxShadow: "0 4px 16px rgba(251,191,36,0.18)",
    transition: "transform 0.18s cubic-bezier(.4,2,.6,1), box-shadow 0.18s cubic-bezier(.4,2,.6,1)",
    letterSpacing: "0.5px",
    outline: "none",
    borderBottom: "3px solid #f97316",
    position: "relative",
    zIndex: 2
  }}
  onClick={() => navigate('/request-demo-class')}
>
  Reserve a Free Demo Class
</button>

  <a
    href="https://wa.me/14256864893"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Chat with us on WhatsApp"
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      padding: isPhone ? "6px 10px" : "8px 14px",
      fontSize: isPhone ? 11 : 12,
      border: "1px solid #25D366",
      borderRadius: 888,
      fontWeight: 700,
      color: "#25D366",
      background: "#fff",
      textDecoration: "none",
      flexWrap: "wrap",
      lineHeight: 1.2,
      whiteSpace: "nowrap",
    }}
    onClick={() => track.ev("CTA_Click", { cta: "WhatsApp", placement: "TopNav", ...getTrackingContext() })}
    onMouseEnter={e => e.currentTarget.style.background = "#f0fff5"}
    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
  >
    <img
      src="/images/misc/whatsapp-svgrepo-com.svg"
      alt="WhatsApp"
      width="18"
      height="18"
    />
    +1&nbsp;425&nbsp;686&nbsp;4893
  </a>
</div>
        <Link to="/login"
      style={{ ...sx.loginLink, ...(isPhone ? sx.loginLinkPhone : null) }}
      onMouseEnter={e => Object.assign(e.currentTarget.style, sx.loginLinkHover)}
      onFocus={e => Object.assign(e.currentTarget.style, sx.loginLinkHover)}
      onMouseLeave={e => Object.assign(e.currentTarget.style, { ...sx.loginLink, ...(isPhone ? sx.loginLinkPhone : null) })}
      onBlur={e => Object.assign(e.currentTarget.style, { ...sx.loginLink, ...(isPhone ? sx.loginLinkPhone : null) })}
      onClick={() => track.ev("CTA_Click", { cta: "Login", placement: "TopNav", ...getTrackingContext() })}
    >
      Log In
    </Link>

        <Link to="/tutor-apply"
      style={{ ...sx.loginLink, ...(isPhone ? sx.loginLinkPhone : null) }}
      onMouseEnter={e => Object.assign(e.currentTarget.style, sx.loginLinkHover)}
      onFocus={e => Object.assign(e.currentTarget.style, sx.loginLinkHover)}
      onMouseLeave={e => Object.assign(e.currentTarget.style, { ...sx.loginLink, ...(isPhone ? sx.loginLinkPhone : null) })}
      onBlur={e => Object.assign(e.currentTarget.style, { ...sx.loginLink, ...(isPhone ? sx.loginLinkPhone : null) })}
      onClick={() => track.ev("CTA_Click", { cta: "TutorApply", placement: "TopNav", ...getTrackingContext() })}
    >
      Join as a Tutor
    </Link>
  </div>
</div>

      {/* ===== HERO (warm gradient, big CTA, social proof badges) ===== */}
<section style={sx.hero}>
  <div style={sx.heroBg} />

  {/* playful background “scene” objects */}
  <span style={{ ...sx.sticker, top: 18, left: 22 }}>🤖</span>
  <span style={{ ...sx.sticker, top: 40, right: 40, transform: "rotate(10deg)" }}>🧩</span>
  <span style={{ ...sx.sticker, bottom: 26, left: 60, fontSize: 52 }}>🚀</span>
  <span style={{ ...sx.sticker, bottom: 34, right: 70 }}>🔢</span>

  <div
  style={{
    position: "relative",
    zIndex: 1,
    ...sx.heroInner,
    ...(isWide ? sx.heroGrid : isTablet ? sx.heroGridTablet : sx.heroStack),
    ...(isPhone ? sx.heroInnerPhone : isTablet ? sx.heroInnerTablet : null),
  }}
>

    {/* LEFT: text + CTA */}
{/* LEFT: text + CTA */}
<div style={{ ...(isWide ? sx.heroLeft : {}), ...(isPhone ? sx.heroLeftPhone : null) }}>

  <h1 style={{ ...sx.h1, ...(isPhone ? sx.h1Mobile : isTablet ? sx.h1Tablet : null) }}>
    {headlineLine.pre}
    <span style={sx.highlight}><b>{headlineLine.highlight}</b></span>
    {headlineLine.post}
  </h1>

  <p style={{ ...sx.sub, ...(isPhone ? sx.subMobile : isTablet ? sx.subTablet : null) }}>
    {marketingLine}
  </p>

  {!isSignedIn ? (
    <div style={{ ...sx.ctaRow, ...(isPhone ? sx.ctaRowPhone : isTablet ? sx.ctaRowTablet : null) }}>
      <button
  style={{
    background: "linear-gradient(90deg, #e0f2fe 0%, #60a5fa 100%)", // light blue to blue
    color: "#1e3a8a",
    border: "none",
    borderRadius: 999,
    padding: isPhone ? "8px 16px" : "14px 28px",
    fontWeight: 700,
    fontSize: isPhone ? 13 : 16,
    cursor: "pointer",
    marginRight: 12,
    boxShadow: "0 2px 8px rgba(96,165,250,0.08)", // softer shadow
    transition: "transform 0.18s cubic-bezier(.4,2,.6,1), box-shadow 0.18s cubic-bezier(.4,2,.6,1)",
    letterSpacing: "0.5px",
    outline: "none",
    borderBottom: "2px solid #60a5fa",
    position: "relative",
    zIndex: 2
  }}
  onMouseEnter={e => {
    e.currentTarget.style.transform = "scale(1.03)";
    e.currentTarget.style.boxShadow = "0 4px 16px rgba(96,165,250,0.16)";
    e.currentTarget.style.background = "linear-gradient(90deg, #60a5fa 0%, #e0f2fe 100%)";
  }}
  onMouseLeave={e => {
    e.currentTarget.style.transform = "scale(1)";
    e.currentTarget.style.boxShadow = "0 2px 8px rgba(96,165,250,0.08)";
    e.currentTarget.style.background = "linear-gradient(90deg, #e0f2fe 0%, #60a5fa 100%)";
  }}
  onBlur={e => {
    e.currentTarget.style.transform = "scale(1)";
    e.currentTarget.style.boxShadow = "0 2px 8px rgba(96,165,250,0.08)";
    e.currentTarget.style.background = "linear-gradient(90deg, #e0f2fe 0%, #60a5fa 100%)";
  }}
  onFocus={e => {
    e.currentTarget.style.transform = "scale(1.03)";
    e.currentTarget.style.boxShadow = "0 4px 16px rgba(96,165,250,0.16)";
    e.currentTarget.style.background = "linear-gradient(90deg, #60a5fa 0%, #e0f2fe 100%)";
  }}
  onClick={() => {
    track.ev("CTA_Click", { cta: "RequestDemo", placement: "Hero", ...getTrackingContext() });
    navigate("/request-demo-class");
  }}
>
  Book a Free Demo
</button>

      <div style={{ ...sx.secondaryCtaCol, ...(isPhone ? sx.secondaryCtaColPhone : null) }}>
        <button
          style={{ ...sx.ctaGhost, ...sx.ctaGhostStack, ...(isPhone ? sx.ctaGhostPhone : null) }}
          onMouseEnter={e => Object.assign(e.currentTarget.style, sx.ctaGhostHover)}
          onMouseLeave={e => Object.assign(e.currentTarget.style, { ...sx.ctaGhost, ...sx.ctaGhostStack, ...(isPhone ? sx.ctaGhostPhone : null) })}
          onFocus={e => Object.assign(e.currentTarget.style, sx.ctaGhostHover)}
          onBlur={e => Object.assign(e.currentTarget.style, { ...sx.ctaGhost, ...sx.ctaGhostStack, ...(isPhone ? sx.ctaGhostPhone : null) })}
          onClick={() => {
            track.ev("CTA_Click", { cta: "StartFreeAccount", placement: "Hero", ...getTrackingContext() });
            goSignup();
          }}
        >
          <span>Create Free Account</span>
          <span style={{ ...sx.ctaGhostMicro, ...(isPhone ? sx.ctaGhostMicroPhone : null) }}>
            Start online tutoring support
          </span>
        </button>
      </div>
    </div>
  ) : (
    <div style={{ ...sx.ctaRow, ...(isPhone ? sx.ctaRowPhone : null) }}>
      <button
        style={{ ...sx.ctaPrimary, ...(isPhone ? sx.ctaPrimaryPhone : null) }}
        onClick={goDashboard}
      >
        Go to my dashboard
      </button>
    </div>
  )}

  <div style={{ ...sx.badges, ...(isPhone ? sx.badgesPhone : null) }}>
    <span>✅ Trusted by parents</span><span style={{ ...sx.dot, ...(isPhone ? sx.dotPhone : null) }}>•</span>
    <span>🔒 Privacy-first</span><span style={{ ...sx.dot, ...(isPhone ? sx.dotPhone : null) }}>•</span>
    <span>🧒 Kid-safe, no ads</span><span style={{ ...sx.dot, ...(isPhone ? sx.dotPhone : null) }}>•</span>
    <span>🗓️ Cancel anytime</span>
  </div>
  <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
  
</div>
  {isPhone && (
  <div style={sx.heroGalleryPhoneWrap}>
    <HeroGallery
      images={slides}
      stageHeight={240}
      objectFit="contain"
      intervalMs={4200}
    />
  </div>
)}
</div>

{/* RIGHT: slideshow */}
{/* RIGHT: slideshow */}
{/* RIGHT: slideshow for desktop / tablet only */}

{!isPhone && (
  <div style={sx.heroRight}>
    <HeroGallery
      images={slides}
      stageHeight={isWide ? 420 : 300}
      objectFit="contain"
      intervalMs={4200}
    />
  </div>
)}
  </div>
</section>


      {/* ===== 3 Feature Cards (colorful like Amazon/Khan) ===== */}
{showLowerHome && (
  <>
<section style={sx.sectionLight}>
  <div style={sx.cards}>
    <Card
      color="#FEF3C7"
      icon="📆"
      title="Homework help after school"
      text="A tutor can walk through confusing questions and help your child understand the next step."
    />
    <Card
      color="#E0F2FE"
      icon="⚡"
      title="Live one-on-one guidance"
      text="Personal tutor support helps students ask questions, clear doubts, and build confidence."
    />
    <Card
      color="#DCFCE7"
      icon="🎧"
      title="Practice support included"
      text="Optional quizzes and progress tools reinforce what students cover with their tutor."
    />
  </div>
</section>

      {/* ===== Testimonial carousel (lightweight, auto-rotate) ===== */}
      <Testimonials />

      {/* ===== Subjects chips ===== */}
      <section style={sx.sectionWhite}>
        <h2 style={sx.h2}>Covers what parents care about</h2>
        <div style={sx.chips}>
          {["Math","Science","Geography","History","Reading","AI Basics","Astronomy","Coding"].map(s => (
            <span key={s} style={sx.chip}>{s}</span>
          ))}
        </div>
        <p style={sx.note}>Grades KG–10 • Live tutoring with optional practice support</p>
      </section>

      {/* ===== Pricing band (side-by-side, yearly highlighted) ===== */}
      {!isSignedIn && (
        <section style={sx.pricingBand}>
          <div style={sx.planCardLeft}>
            <div style={sx.planTitle}>Monthly</div>
            <div><span style={sx.priceBig}>$12.99</span></div>
            <div style={sx.planNote}>Great to try it out</div>
          </div>
          <div style={sx.planCardRight}>
            <div style={sx.bestTag}>Best Value</div>
            <div style={sx.planTitle}>Yearly</div>
            <div><span style={sx.priceBig}>$99.99</span></div>
            <div style={sx.planNote}>Save more.</div>
          </div>
          <div style={{display:"grid", placeItems:"center"}}>
            <button style={sx.ctaPrimary} onClick={goSignup}>Start Tutoring Support</button>
            <div style={{fontSize:12, color:"#64748b", marginTop:8}}>No card required • Cancel anytime</div>
          </div>
        </section>
      )}

      {/* ===== FAQs (contrast section) ===== */}
      <section style={sx.sectionFAQ}>
        <h2 style={sx.h2}>Questions parents ask</h2>

        <FAQ q="Do I need to sit with my child?">
          No. TutorOrca is built to support independent learning with live tutor guidance and parent visibility.
        </FAQ>
        <FAQ q="How much time per day is enough?">
          A steady routine helps. Many families start with regular tutoring sessions and short practice between classes.
        </FAQ>
        <FAQ q="Is it safe and private?">
          Yes. No ads, no social features, and we never sell data. You can delete your account anytime.
        </FAQ>
        <FAQ q="Which grades and subjects?">
          Grades KG–10 across Math, Science, English, Geography, History, Reading, AI basics, and more.
        </FAQ>
      </section>

      {/* ===== Footer with policies ===== */}
      <footer style={sx.footer}>
        <p style={{marginBottom:8}}>
          One-on-one online tutoring and practice support for confident KG–10 learning.{" "}
          <Link to="/about">Learn more</Link>
        </p>
        <nav style={{marginBottom:8}}>
  <Link
    to="/privacy-policy"
    style={sx.footerLink}
    onMouseEnter={e => e.currentTarget.style.color = sx.footerLinkHover.color}
    onMouseLeave={e => e.currentTarget.style.color = sx.footerLink.color}
  >
    Privacy Policy
  </Link>{" "}·{" "}
  <Link
    to="/cookie-policy"
    style={sx.footerLink}
    onMouseEnter={e => e.currentTarget.style.color = sx.footerLinkHover.color}
    onMouseLeave={e => e.currentTarget.style.color = sx.footerLink.color}
  >
    Cookie Policy
  </Link>{" "}·{" "}
  <Link
    to="/terms-of-use"
    style={sx.footerLink}
    onMouseEnter={e => e.currentTarget.style.color = sx.footerLinkHover.color}
    onMouseLeave={e => e.currentTarget.style.color = sx.footerLink.color}
  >
    Terms of Use
  </Link>
</nav>

        <p>© {new Date().getFullYear()} TutorOrca</p>
      </footer>
  </>
)}
      {demoModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 320
          }}>
            <h3 style={{ marginBottom: 18 }}>Reserve a Free Demo Class Now</h3>
            <label>Subject:</label>
            <select value={demoSubject} onChange={e => setDemoSubject(e.target.value)} style={{ width: '100%', marginBottom: 16 }}>
              <option value="">Select subject</option>
              <option value="Math">Math</option>
              <option value="Science">Science</option>
              <option value="English">English</option>
              <option value="History">History</option>
              <option value="Dance">Dance</option>
              <option value="Art">Art</option>
            </select>
            <label>Grade:</label>
            <select value={demoGrade} onChange={e => setDemoGrade(e.target.value)} style={{ width: '100%', marginBottom: 16 }}>
              <option value="">Select grade</option>
              {demoSubject === "Art" ? (
                <>
                  <option value="Hina">Hina</option>
                  <option value="Crochet">Crochet</option>
                  <option value="Knitting">Knitting</option>
                </>
              ) : (
                <>
                  <option value="1stGrade">1st Grade</option>
                  <option value="2ndGrade">2nd Grade</option>
                  <option value="3rdGrade">3rd Grade</option>
                  <option value="4thGrade">4th Grade</option>
                  <option value="5thGrade">5th Grade</option>
                  <option value="6thGrade">6th Grade</option>
                  <option value="7thGrade">7th Grade</option>
                  <option value="8thGrade">8th Grade</option>
                </>
              )}
            </select>
            {!localStorage.getItem('userId') && (
              <>
                <label>Email Address:</label>
                <input
                  type="email"
                  value={demoEmail}
                  onChange={e => setDemoEmail(e.target.value)}
                  style={{ width: '100%', marginBottom: 16 }}
                  placeholder="Enter your email"
                  required
                />
              </>
            )}
            <div style={{ display: 'flex', gap: 12, marginTop: 18 }}>
              <button
                style={{
                  background: demoSubmitting ? '#bdbdbd' : '#1976d2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 18px',
                  fontWeight: 600,
                  cursor: demoSubmitting ? 'not-allowed' : (!demoSubject || !demoGrade || (!localStorage.getItem('userId') && !demoEmail)) ? 'not-allowed' : 'pointer',
                  opacity: demoSubmitting ? 0.7 : 1,
                  position: 'relative'
                }}
                onClick={() => {
                  setDemoSubmitting(true);
                  setDemoModalOpen(false);
                  navigate('/request-demo-class', {
                    state: { email: demoEmail || localStorage.getItem('userEmail') || '' }
                  });
                  setDemoSubmitting(false);
                }}
                disabled={
                  !demoSubject ||
                  !demoGrade ||
                  demoSubmitting ||
                  (!localStorage.getItem('userId') && !demoEmail)
                }
              >
                {demoSubmitting ? (
                  <>
                    <span className="spinner" style={{
                      display: 'inline-block',
                      width: 16,
                      height: 16,
                      border: '2px solid #fff',
                      borderRadius: '50%',
                      borderTop: '2px solid #1976d2',
                      animation: 'spin 1s linear infinite',
                      marginRight: 8,
                      verticalAlign: 'middle'
                    }} />
                    Request is being submitted...
                  </>
                ) : (
                  'Submit Request'
                )}
              </button>
              <button
                style={{ background: '#e0e0e0', color: '#222', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600 }}
                onClick={() => { setDemoModalOpen(false); resetDemoModal(); }}
              >
                Cancel
              </button>
            </div>
            {demoNotify && (
              <div style={{ marginTop: 18, color: '#1976d2', fontWeight: 600 }}>
                {demoNotify}
                {demoNotify === 'Demo request submitted! We will notify you once it is scheduled.' && (
                  <div style={{ marginTop: 16 }}>
                    <button
                      style={{
                        background: '#1976d2',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '8px 18px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                      onClick={() => { setDemoModalOpen(false); resetDemoModal(); }}
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
  </div>
  );
}

/* ===== Small pieces ===== */
function Card({ color, icon, title, text }) {
  return (
    <div style={{...sx.card, background: color}}>
      <div style={sx.cardIcon}>{icon}</div>
      <div>
        <div style={sx.cardTitle}>{title}</div>
        <div style={sx.cardText}>{text}</div>
      </div>
    </div>
  );
}

function FAQ({ q, children }) {
  return (
    <details style={sx.faqItem}>
      <summary style={sx.faqSummary}>{q}</summary>
      <div style={sx.faqAnswer}>{children}</div>
    </details>
  );
}

function Testimonials() {
  const quotes = [
    { by: "Neha, mom of 5th Grader", txt: "The tutor helped my child slow down, ask questions, and finally understand the homework.", stars: 5 },
    { by: "Prabha, mom of 9th Grader", txt: "Regular online sessions made study time calmer, and I can see steady confidence building.", stars: 5 },
    { by: "Shweta, mom of 8th grader", txt: "One-on-one guidance keeps my son engaged. The extra practice helps between classes.", stars: 5 }
  ];
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI(p => (p+1)%quotes.length), 4000); return () => clearInterval(t); }, [quotes.length]);
  const q = quotes[i];
  return (
    <section style={sx.sectionWarm}>
      <div style={sx.testiCard}>
        <div style={sx.stars}>{"★".repeat(q.stars)}{"☆".repeat(5-q.stars)}</div>
        <div style={sx.testiTxt}>"{q.txt}"</div>
        <div style={sx.testiBy}>— {q.by}</div>
        
      </div>
    </section>
  );
}



// Amazon-ish palette
// Dark header: #131921 / #232F3E
// Accent:      #FFA41C (Amazon orange)
// Links:       #007185
// Page bg:     #EAEDED
// Card/surfaces: #FFFFFF
// Borders:     #D5D9D9
// Text:        #0F1111 / #565959 (muted)
const UI = {
  border: "1px solid rgba(15,17,17,.12)",
  borderStrong: "1px solid rgba(15,17,17,.18)",
  radiusCard: 22,
  radiusMd: 14,
  radiusPill: 999,
  shadowCard: "0 18px 50px rgba(15,17,17,.10)",
  shadowSoft: "0 10px 28px rgba(15,17,17,.08)",
};

const sx = {
  h1: {
  fontSize: 42,  // bolder headline for impact
  fontWeight: 700,
  lineHeight: 1.25,
  color: "#0F1111", // clean dark text
  textAlign: "left", // optional
  marginBottom: 16,
}
,
secondaryCtaCol: {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  maxWidth: 290,
  flex: "0 0 290px",
},

    loginLink: {
  color: "#e5e7eb",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 12px",
  border: "1px solid rgba(255,255,255,.22)",
  background: "rgba(255,255,255,.06)",
  borderRadius: 999,
  fontWeight: 700,
  lineHeight: 1
},
heroGalleryPhoneWrap: {
  marginTop: 14,
  width: "100%",
  minHeight: 240,
  display: "block",
},
loginLinkPhone: {
  padding: "6px 10px",
  fontSize: 13,
},
  loginLinkHover: {
    background: "rgba(255,255,255,.14)",
    color: "#ffffff",
    borderColor: "rgba(255,255,255,.35)"
  },
  // Page background gives the left/right "gutters" like Amazon
page: {
  background: "linear-gradient(180deg, #EAF6FF 0%, #FFF7ED 55%, #FFFFFF 100%)",
  minHeight: "100vh"
},

  // Urgency strip styled like Amazon's dark bars
  urgencyBar: {
    position:"sticky", top:0, zIndex:10, display:"flex", justifyContent:"space-between",
    alignItems:"center", padding:"10px 16px",
    background:"#232F3E", color:"#FFFFFF"
  },
  saleBadge: {
    background:"#FFA41C", color:"#111111",
    padding:"2px 8px", borderRadius:999, fontWeight:800, fontSize:12
  },
  urgencyBtn: {
    background:"#FFA41C", color:"#111111", border:"1px solid #E47911",
    borderRadius:10, padding:"10px 14px", fontWeight:700, cursor:"pointer"
  },
  

  // HERO: light warm neutral (no cool blue/purple), shows page gutters at sides
hero: {
  background: "linear-gradient(180deg, #EAF6FF 0%, #FFF7ED 65%, #FDFDFE 100%)",
  padding: "44px 16px 30px",
  position: "relative",
  overflow: "hidden",
},

heroInner: {
  maxWidth: 1280,
  margin: "0 auto",
  textAlign: "center",
  background: "rgba(255,255,255,.94)",
  backdropFilter: "blur(7px)",
  padding: "30px 24px",
  borderRadius: UI.radiusCard,
  boxShadow: UI.shadowCard,
  border: UI.border,
},

heroBg: {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  zIndex: 0,
  background:
    "radial-gradient(circle at 15% 20%, rgba(34,211,238,.28) 0%, rgba(34,211,238,0) 42%)," +
    "radial-gradient(circle at 85% 25%, rgba(124,58,237,.22) 0%, rgba(124,58,237,0) 44%)," +
    "radial-gradient(circle at 30% 90%, rgba(251,191,36,.26) 0%, rgba(251,191,36,0) 46%)," +
    "radial-gradient(circle at 80% 80%, rgba(251,113,133,.22) 0%, rgba(251,113,133,0) 46%)",
},

sticker: {
  position: "absolute",
  zIndex: 0,
  fontSize: 44,
  opacity: 0.18,
  filter: "blur(.2px)",
  transform: "rotate(-8deg)",
},
  
  highlight: { color:"#7C3AED" },

  sub: { marginTop:10, fontSize:18, color:"#565959" },

  ctaRow: {
  marginTop: 18,
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "center",
},
ctaPrimary: {
  background: "linear-gradient(135deg, #facc15 0%, #f97316 100%)",
  color: "#fff",
  border: "none",
  borderRadius: 999,
  padding: "15px 22px",
  fontWeight: 800,
  fontSize: 16,
  letterSpacing: "0.2px",
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(251, 191, 36, .22)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  minHeight: 60,
  whiteSpace: "nowrap",
},

  ctaPrimaryHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 10px 24px rgba(251, 146, 60, .6)", // deeper orange glow
  },

  ctaSecondary: {
    background: "linear-gradient(135deg, #22d3ee 0%, #10b981 100%)", // fun teal → green gradient
    color: "#ffffff",
    border: "none",
    borderRadius: 999, // pill shape
    padding: "14px 28px",
    fontWeight: 800,
    fontSize: 17,
    letterSpacing: "0.3px",
    cursor: "pointer",
    boxShadow: "0 6px 16px rgba(34,211,238,.35)", // glow
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  },

  // Add a hover state for interactivity
  ctaSecondaryHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(34,211,238,.5)"
  },

ctaGhost: {
  background: "rgba(124,58,237,.05)",
  color: "#1F2A44",
  border: "1px solid rgba(124,58,237,.22)",
  borderRadius: 999,
  padding: "12px 18px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 8px 18px rgba(124,58,237,.08)",
  transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease, background .15s ease",
  minHeight: 64,
  width: "100%",
},
ctaGhostHover: {
  transform: "translateY(-1px)",
  background: "rgba(124,58,237,.10)",
  border: "1px solid rgba(124,58,237,.32)",
  boxShadow: "0 14px 28px rgba(124,58,237,.12)",
},


  badges:{ marginTop:14, display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center", color:"#565959", fontSize:14 },

  // Section bands use soft grays (no saturated greens/browns)
  sectionLight:{ background:"#F3F3F3", padding:"22px 16px" },
  sectionWhite:{ background:"#FFFFFF", padding:"22px 16px" },
  sectionWarm:{ background:"#F8F8F8", padding:"26px 16px" },
  sectionFAQ:{ background:"#F6F6F6", padding:"28px 16px" },

  // Cards
  cards:{
    maxWidth:1100, margin:"0 auto", display:"grid",
    gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:16
  },
  card:{
    display:"flex", gap:12, padding:16, borderRadius:14,
    boxShadow:"0 8px 24px rgba(15,17,17,.06)",
    border:"1px solid #D5D9D9"
  },
  cardIcon:{ fontSize:28, lineHeight:1 },
  cardTitle:{ fontWeight:800, fontSize:18, marginBottom:4, color:"#0F1111" },
  cardText:{ color:"#565959" },

  // Testimonials
  testiCard:{
    maxWidth:820, margin:"0 auto", background:"#FFFFFF", border:"1px solid #D5D9D9",
    borderRadius:14, padding:20, textAlign:"center",
    boxShadow:"0 10px 28px rgba(15,17,17,.06)"
  },
  stars:{ color:"#FFA41C", letterSpacing:2, marginBottom:8, fontSize:18 },
  testiTxt:{ fontStyle:"italic", color:"#0F1111" },
  testiBy:{ marginTop:6, color:"#565959" },

  // “Tabs” like Amazon → pill chips with gray bg/border, link-blue text on hover
  chips:{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center", maxWidth:1000, margin:"10px auto 0" },
chip: {
  background: "rgba(255,255,255,.75)",
  color: "#0F172A",
  padding: "9px 13px",
  borderRadius: 999,
  fontWeight: 800,
  fontSize: 14,
  border: "1px solid rgba(124,58,237,.18)",
  boxShadow: "0 8px 18px rgba(124,58,237,.08)",
  transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
},
  note:{ color:"#565959", textAlign:"center", marginTop:6 },

  // Pricing band
  pricingBand:{
    background:"#FFFFFF", padding:"18px 16px", display:"grid", gap:14,
    gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", alignItems:"stretch",
    maxWidth:1100, margin:"14px auto", border:"1px solid #D5D9D9", borderRadius:14
  },
  planCardLeft:{ background:"#F7F8F8", border:"1px solid #D5D9D9", borderRadius:12, padding:16 },
  planCardRight:{ background:"#FFF", border:"2px solid #FFA41C", borderRadius:12, padding:16, position:"relative" },
  planTitle:{ fontWeight:800, color:"#0F1111", marginBottom:6 },
  priceBig:{ fontSize:28, fontWeight:900, color:"#0F1111" },
  planNote:{ color:"#565959", marginTop:6 },
  bestTag:{ position:"absolute", top:10, right:10, background:"#FFF7E0", color:"#C45500", fontWeight:800, fontSize:12, padding:"2px 8px", borderRadius:999, border:"1px solid #FFD08A" },

  // FAQ
  faqItem:{ maxWidth:900, margin:"10px auto", background:"#FFFFFF", border:"1px solid #D5D9D9", borderRadius:12, padding:"10px 12px" },
  faqSummary:{ cursor:"pointer", fontWeight:800, color:"#0F1111" },
  faqAnswer:{ marginTop:8, color:"#565959" },

  // Footer
  footer:{ textAlign:"center", padding:"28px 0", color:"#565959", background:"#FFFFFF", borderTop:"1px solid #D5D9D9" },
  footerLink: {
  color: "#007185",    // Amazon-like blue
  textDecoration: "none",
  fontSize: 14,
  transition: "color 0.2s ease"
},
footerLinkHover: {
  color: "#C45500"     // warm hover (Amazon orange/brown)
},
navLink: {
  color:"#fff",
  fontSize: 15,
  fontWeight: 500,
  textDecoration:"none",
  padding:"6px 10px",
  borderRadius:4,
  transition:"background 0.2s ease, color 0.2s ease",
  cursor:"pointer"
},
navLinkPhone: {
  fontSize: 13,
  padding: "4px 6px",
},
navLinkHover: {
  background: "rgba(255,255,255,.14)",
  color: "#FBBF24"
},

  // --- slideshow ---

  dots: {
  position: "absolute",
  bottom: 8,
  left: 44,
  right: 44,
  display: "flex",
  gap: 6,
  overflowX: "auto",
  justifyContent: "flex-start",
  padding: "0 6px",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
},
dot: {
  width: 9,
  height: 9,
  borderRadius: 999,
  border: "1px solid rgba(0,0,0,.25)",
  background: "rgba(255,255,255,.6)",
  cursor: "pointer",
  flex: "0 0 auto",
},
galleryCount: {
  position: "absolute",
  right: 10,
  top: 10,
  zIndex: 2,
  padding: "4px 8px",
  borderRadius: 999,
  background: "rgba(255,255,255,.92)",
  border: "1px solid rgba(0,0,0,.12)",
  fontSize: 12,
  fontWeight: 700,
  color: "#0F1111",
},
dotActive: {
  background: "#7C3AED",
  border: "1px solid #4C1D95"
},
  arrow: {
    position: "absolute", top: "calc(50% - 18px)",
    width: 36, height: 36, borderRadius: 999,
    border: "1px solid rgba(0,0,0,.2)",
    background: "rgba(255,255,255,.85)",
    color: "#0F1111", fontWeight: 900, fontSize: 20,
    lineHeight: "34px", textAlign: "center",
    cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,.12)"
  },
    // Hero two-column layout on desktop
  heroGrid: {
    display: "grid",
    gridTemplateColumns: "1.5fr 1fr",
    alignItems: "center",
    gap: 24,
    textAlign: "left"
  },
  heroLeft: { },
heroRight: {
  justifySelf: "center",
  maxWidth: "100%",
  width: "100%",          // ✅ contain it properly
  margin: "0 auto",       // ✅ center horizontally
},


  // Slideshow: slightly smaller + framed look
  slideWrap: { /* keep existing, but ensure a max width for side column */ 
    position:"relative", maxWidth: 520, width:"100%",
    margin:"12px auto 0", borderRadius:14, border:"1px solid #D5D9D9",
    boxShadow:"0 12px 28px rgba(15,17,17,.08)", overflow:"hidden",
     background: "#fff",
  minHeight: 200  // remove after confirming
  },
  slideStage: {
    position:"relative", width:"100%", paddingTop:"56.25%" // will be overridden by fixed height
  },
// scroll-snap gallery
galleryWrap: {
  position: "relative",
  maxWidth: 640,
  width: "100%",
  margin: "12px auto 0",
  borderRadius: UI.radiusMd,
  border: UI.border,
  boxShadow: UI.shadowSoft,
  overflow: "hidden",
  background: "#fff"
},

galleryTrack: {
  display: "flex",
  overflowX: "auto",
  scrollSnapType: "x mandatory",
  scrollbarWidth: "thin",
  scrollBehavior: "smooth", // <—
},
gallerySlide: {
  flex: "0 0 100%",
  width: "100%",
  height: "100%",
  scrollSnapAlign: "center",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "center",
  background: "#fff"   // keep it clean, no gradient gap
},
galleryEmpty: {
  display: "grid",
  placeItems: "center",
  width: "100%",
  height: "100%",
  color: "#64748b",
  fontSize: 14
},
ctaGhostStack: {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  paddingTop: 10,
  paddingBottom: 10,
  width: "100%",
},
ctaGhostMicro: {
  fontSize: 12,
  color: "rgba(15,17,17,.60)",
  lineHeight: 1.2,
  textAlign: "center",
},
heroStack: {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 16,
  textAlign: "left",
},

heroInnerPhone: {
  padding: "16px 14px 18px",
  borderRadius: 18,
},

heroLeftPhone: {
  textAlign: "left",
},

heroRightPhone: {
  marginTop: 14,
  width: "100%",
},

h1Mobile: {
  fontSize: 30,
  lineHeight: 1.18,
  marginBottom: 10,
},

subMobile: {
  fontSize: 16,
  marginTop: 6,
},

ctaRowPhone: {
  flexDirection: "column",
  alignItems: "stretch",
  gap: 10,
},

ctaPrimaryPhone: {
  width: "100%",
  padding: "14px 16px",
  fontSize: 16,
  minHeight: 56,
},
ctaGhostMicroPhone: {
  fontSize: 11,
},
secondaryCtaColPhone: {
  maxWidth: "100%",
  width: "100%",
  alignItems: "stretch",
  flex: "none",
},

ctaGhostPhone: {
  width: "100%",
  minHeight: "auto",
},

badgesPhone: {
  justifyContent: "flex-start",
  fontSize: 13,
  gap: 8,
  display: "grid",
  gridTemplateColumns: "1fr",
},

dotPhone: {
  display: "none",
},
heroGridTablet: {
  display: "grid",
  gridTemplateColumns: "1.2fr 1fr",
  alignItems: "center",
  gap: 18,
  textAlign: "left",
},

heroInnerTablet: {
  padding: "22px 18px",
  borderRadius: 20,
},

h1Tablet: {
  fontSize: 36,
  lineHeight: 1.2,
  marginBottom: 12,
},

subTablet: {
  fontSize: 17,
},
ctaRowTablet: {
  justifyContent: "center",
  alignItems: "center",
  gap: 12,
  flexWrap: "nowrap",
},

};
export default Home;
